import React, { useState, useEffect, useCallback } from 'react';
import { formatearMoneda, limpiarMoneda } from '../utils/formatMoney';
import useResponsive from '../utils/useResponsive';
import CuestionarioCualitativo from './santander/CuestionarioCualitativo';
import { esMexicana } from '../utils/calculoRfcCurp';
import { creditoExcedeInmueble, MENSAJE_CREDITO_INMUEBLE } from '../utils/creditoVsInmueble';

// ── Preguntas cuestionario médico ────────────────────────────────────────────
const MED_PREGUNTAS = [
  { key: '1a',  grupo: 'A', texto: '¿Padece o ha padecido enfermedades del corazón o sistema circulatorio?' },
  { key: '2a',  grupo: 'A', texto: '¿Padece o ha padecido diabetes mellitus?' },
  { key: '3a',  grupo: 'A', texto: '¿Padece o ha padecido cáncer, tumores o neoplasias?' },
  { key: '4a',  grupo: 'A', texto: '¿Padece o ha padecido enfermedades de los riñones, hígado o vías urinarias?' },
  { key: '5a',  grupo: 'A', texto: '¿Padece o ha padecido VIH/SIDA o hepatitis?' },
  { key: '6b',  grupo: 'B', texto: '¿Padece o ha padecido enfermedades del sistema nervioso, psiquiátricas o psicológicas?' },
  { key: '7b',  grupo: 'B', texto: '¿Padece o ha padecido enfermedades del aparato respiratorio?' },
  { key: '8b',  grupo: 'B', texto: '¿Se encuentra bajo tratamiento médico o tiene programada una cirugía?' },
  { key: '9b',  grupo: 'B', texto: '¿Ha tenido alguna intervención quirúrgica en los últimos 2 años?' },
  { key: '10b', grupo: 'B', texto: '¿Tiene alguna discapacidad física o sensorial?' },
  { key: '11b', grupo: 'B', texto: '¿Consume tabaco, alcohol u otras sustancias de manera regular?' },
  { key: '12b', grupo: 'B', texto: '¿Practica algún deporte de alto riesgo o actividad peligrosa?' },
  { key: '13b', grupo: 'B', texto: '¿Está usted embarazada o en período de lactancia?' },
  { key: '14c', grupo: 'C', texto: '¿Ha tenido alguna reclamación de seguro de vida o invalidez?' },
];

const stBase = {
  card: { backgroundColor: '#F0F0F0', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#1a3a6e', marginBottom: 16, borderBottom: '2px solid #e11d48', paddingBottom: 8 },
  subtitle: { fontSize: 15, fontWeight: '600', color: '#374151', marginTop: 16, marginBottom: 8 },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  inputNum: { width: '100%', height: '42px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  readOnly: { backgroundColor: '#f3f4f6', fontWeight: 'bold' },
  moneyWrap: { position: 'relative' },
  moneySign: { position: 'absolute', left: 12, top: 11, color: '#6b7280', fontSize: 14 },
  saveBtn: { backgroundColor: '#1a3a6e', color: 'white', border: 'none', padding: '12px 24px', borderRadius: 6, fontSize: 15, fontWeight: '600', cursor: 'pointer', marginTop: 8 },
  preguntaRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', gap: 16 },
  preguntaLabel: { flex: 1, fontSize: 13, color: '#374151' },
  grupoTag: { fontSize: 11, fontWeight: 'bold', color: 'white', backgroundColor: '#1a3a6e', borderRadius: 4, padding: '2px 6px', marginRight: 8 },
};

const SANTANDER_ESTADO_CIVIL_MAP = {
  'Soltero': 'Soltero', 'Divorciado': 'Divorciado', 'Viudo': 'Viudo', 'Casado': 'Casado',
  'Union Libre': 'Union Libre',
  // Sociedad de Convivencia no existe en el catálogo de Santander -> Union Libre.
  'Sociedad de Convivencia': 'Union Libre',
};
const SANTANDER_REGIMEN_MAP = {
  'Sociedad Conyugal': 'Sociedad Conyugal',
  'Separación de Bienes': 'Bienes Separados',
};
const SANTANDER_VIVE_CASA_MAP = {
  'Propia': 'Propia', 'Hipotecada': 'Hipotecada', 'Rentada': 'Rentada',
  'Padres': 'Familiar', 'Otro Parentesco': 'Familiar',
  // 'Otro' del catálogo central no tiene equivalente en Santander — se deja sin mapear.
};
function idPorNombreSantander(catalogo, nombre) {
  if (!nombre) return '';
  const match = catalogo.find(c => c.nombre === nombre);
  return match ? match.id : '';
}

const SiNoRadio = ({ value, onChange, name, defaultNo }) => {
  useEffect(() => {
    if (defaultNo && (value === undefined || value === null)) onChange(false);
  }, [value, defaultNo, onChange]); // eslint-disable-line react-hooks/exhaustive-deps
  const val = (value === undefined || value === null) && defaultNo ? false : value;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={name} checked={val === true} onChange={() => onChange(true)} /> Sí
      </label>
      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
        <input type="radio" name={name} checked={val === false} onChange={() => onChange(false)} /> No
      </label>
    </div>
  );
};

const BancoSantander = ({ prospectoId, formData, setFormData }) => {
  const API = '/api';
  const { isMobile, isTablet } = useResponsive();
  const st = {
    ...stBase,
    grid2: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 16, marginBottom: 16 },
    grid3: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 16, marginBottom: 16 },
    grid4: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
  };

  // Catálogos
  const [nacionalidades,          setNacionalidades]          = useState([]);
  const [escolaridades,           setEscolaridades]           = useState([]);
  const [tiposIdentificacion,     setTiposIdentificacion]     = useState([]);
  const [estadosCiviles,          setEstadosCiviles]          = useState([]);
  const [regimenes,               setRegimenes]               = useState([]);
  const [vivesCasa,               setVivesCasa]               = useState([]);
  const [puestos,                 setPuestos]                 = useState([]);
  const [ocupaciones,             setOcupaciones]             = useState([]);
  const [sectoresPublicos,        setSectoresPublicos]        = useState([]);
  const [tiposContrato,           setTiposContrato]           = useState([]);
  const [girosEmpresa,            setGirosEmpresa]            = useState([]);
  const [destinos,                setDestinos]                = useState([]);
  const [productos,               setProductos]               = useState([]);
  const [esquemasPagos,           setEsquemasPagos]           = useState([]);
  const [plazos,                  setPlazos]                  = useState([]);
  const [programasFinanciamiento, setProgramasFinanciamiento] = useState([]);
  const [tiposCofinanciamiento,   setTiposCofinanciamiento]   = useState([]);
  const [tiposLiquidez,           setTiposLiquidez]           = useState([]);
  const [tiposMejora,             setTiposMejora]             = useState([]);
  const [parentescos,             setParentescos]             = useState([]);
  const [actividadesSeguros,      setActividadesSeguros]      = useState([]);
  const [tiposInmueble,           setTiposInmueble]           = useState([]);
  const [estadosInmueble,         setEstadosInmueble]         = useState([]);
  const [municipiosInmueble,      setMunicipiosInmueble]      = useState([]);

  const [sn, setSn] = useState({});
  const [focusedMoneyField, setFocusedMoneyField] = useState(null);
  const [santanderCargado, setSantanderCargado] = useState(false);

  useEffect(() => { cargarCatalogos(); }, []);

  useEffect(() => {
    if (!prospectoId) { setSantanderCargado(true); return; }
    fetch(`${API}/santander/${prospectoId}`)
      .then(r => r.ok ? r.json() : null)
      .then(datos => {
        if (datos && datos.id) {
          setSn(datos);
          setFormData(prev => ({ ...prev, santander: datos }));
          if (datos.inm_entidad_federativa) {
            cargarMunicipiosInmueble(datos.inm_entidad_federativa);
          }
        }
      })
      .catch(e => console.error('Error cargando datos Santander:', e))
      .finally(() => setSantanderCargado(true));
  }, [prospectoId]);

  // Identificación, Estado Civil, Régimen Matrimonial y Vive en Casa (Titular y
  // Coacreditado) se derivan del módulo Titular / Coacreditado. Se recalculan
  // cuando cambian ahí, y también una vez que terminó de cargar lo guardado de
  // Santander, para no quedar pisados por un valor anterior guardado en la BD.
  useEffect(() => {
    if (!santanderCargado) return;
    if (estadosCiviles.length === 0 || regimenes.length === 0 || vivesCasa.length === 0 || nacionalidades.length === 0) return;

    const derivar = (persona) => {
      const ecCentral = persona?.estado_civil;
      const ecNombre = SANTANDER_ESTADO_CIVIL_MAP[ecCentral] || null;
      const regNombre = ecCentral === 'Casado' ? (SANTANDER_REGIMEN_MAP[persona?.regimen_matrimonio] || null) : null;
      return {
        tipoIdId: persona?.tipo_identificacion_santander_id ? Number(persona.tipo_identificacion_santander_id) : '',
        numId: persona?.numero_identificacion || '',
        ecId: idPorNombreSantander(estadosCiviles, ecNombre),
        regId: idPorNombreSantander(regimenes, regNombre),
        viveCasaId: idPorNombreSantander(vivesCasa, SANTANDER_VIVE_CASA_MAP[persona?.vive_en_casa] || null),
        nacId: idPorNombreSantander(nacionalidades, esMexicana(persona?.nacionalidad) ? 'Mexicano (a)' : 'Extranjero (a)'),
      };
    };
    const tit = derivar(formData.titular);
    const coac = derivar(formData.coacreditado);

    setSn(prev => {
      const next = { ...prev };
      let changed = false;
      const set = (k, v) => { if (next[k] !== v) { next[k] = v; changed = true; } };
      set('titular_tipo_identificacion_id', tit.tipoIdId);
      set('titular_numero_identificacion', tit.numId);
      set('titular_estado_civil_id', tit.ecId);
      set('titular_regimen_matrimonial_id', tit.regId);
      set('titular_vive_casa_id', tit.viveCasaId);
      set('titular_nacionalidad_id', tit.nacId);
      set('coac_tipo_identificacion_id', coac.tipoIdId);
      set('coac_numero_identificacion', coac.numId);
      set('coac_estado_civil_id', coac.ecId);
      set('coac_regimen_matrimonial_id', coac.regId);
      set('coac_vive_casa_id', coac.viveCasaId);
      set('coac_nacionalidad_id', coac.nacId);
      return changed ? next : prev;
    });
  }, [
    santanderCargado, estadosCiviles, regimenes, vivesCasa, nacionalidades,
    formData.titular.estado_civil, formData.titular.regimen_matrimonio, formData.titular.vive_en_casa,
    formData.titular.tipo_identificacion_santander_id, formData.titular.numero_identificacion, formData.titular.nacionalidad,
    formData.coacreditado?.estado_civil, formData.coacreditado?.regimen_matrimonio, formData.coacreditado?.vive_en_casa,
    formData.coacreditado?.tipo_identificacion_santander_id, formData.coacreditado?.numero_identificacion, formData.coacreditado?.nacionalidad,
  ]);

  const cargarCatalogos = async () => {
    try {
      const cats = await Promise.all([
        fetch(`${API}/catalogos/santander/nacionalidad`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/escolaridad`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_identificacion`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/estado_civil`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/regimen_matrimonial`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/vive_casa`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/puesto`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/ocupacion`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/sector_publico`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_contrato`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/giro_empresa`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/destino`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/producto`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/esquema_pagos`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/plazo_credito`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/programa_financiamiento`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_cofinanciamiento`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_liquidez`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_mejora`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/parentesco_funcionario`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/actividad_seguros`).then(r => r.json()),
        fetch(`${API}/catalogos/santander/tipo_inmueble`).then(r => r.json()),
        fetch(`${API}/sepomex/estados`).then(r => r.json()),
      ]);
      setNacionalidades(cats[0]);          setEscolaridades(cats[1]);
      setTiposIdentificacion(cats[2]);     setEstadosCiviles(cats[3]);
      setRegimenes(cats[4]);               setVivesCasa(cats[5]);
      setPuestos(cats[6]);                 setOcupaciones(cats[7]);
      setSectoresPublicos(cats[8]);        setTiposContrato(cats[9]);
      setGirosEmpresa(cats[10]);           setDestinos(cats[11]);
      setProductos(cats[12]);              setEsquemasPagos(cats[13]);
      setPlazos(cats[14]);                 setProgramasFinanciamiento(cats[15]);
      setTiposCofinanciamiento(cats[16]);  setTiposLiquidez(cats[17]);
      setTiposMejora(cats[18]);            setParentescos(cats[19]);
      setActividadesSeguros(cats[20]);     setTiposInmueble(cats[21]);
      setEstadosInmueble((cats[22] || []).map(r => r.estado || r));
    } catch (e) {
      console.error('Error cargando catálogos Santander:', e);
    }
  };

  const cargarMunicipiosInmueble = async (estado) => {
    if (!estado) { setMunicipiosInmueble([]); return; }
    try {
      const r = await fetch(`${API}/sepomex/municipios/${encodeURIComponent(estado)}`);
      const d = await r.json();
      setMunicipiosInmueble(d.map(m => m.municipio || m));
    } catch (e) { console.error('Error cargando municipios inmueble:', e); }
  };

  const upd = useCallback((campo, valor) => {
    setSn(prev => {
      const next = { ...prev, [campo]: valor };
      // Auto-cálculo total no asalariado titular
      if (['titular_ingreso_variable_mensual','titular_ingreso_flujos','titular_otros_ingresos_anuales'].includes(campo)) {
        const v = parseFloat(campo === 'titular_ingreso_variable_mensual' ? valor : next.titular_ingreso_variable_mensual) || 0;
        const f = parseFloat(campo === 'titular_ingreso_flujos' ? valor : next.titular_ingreso_flujos) || 0;
        const o = (parseFloat(campo === 'titular_otros_ingresos_anuales' ? valor : next.titular_otros_ingresos_anuales) || 0) / 12;
        next.titular_total_calculado = Math.round(v + f + o).toString();
      }
      // Auto-cálculo total no asalariado coacreditado
      if (['coac_ingreso_variable_mensual','coac_ingreso_flujos','coac_otros_ingresos_anuales'].includes(campo)) {
        const v = parseFloat(campo === 'coac_ingreso_variable_mensual' ? valor : next.coac_ingreso_variable_mensual) || 0;
        const f = parseFloat(campo === 'coac_ingreso_flujos' ? valor : next.coac_ingreso_flujos) || 0;
        const o = (parseFloat(campo === 'coac_otros_ingresos_anuales' ? valor : next.coac_otros_ingresos_anuales) || 0) / 12;
        next.coac_total_calculado = Math.round(v + f + o).toString();
      }
      return next;
    });
  }, []);

  const guardar = async () => {
    if (!prospectoId) {
      alert('Primero guarde el prospecto antes de agregar información del banco');
      return;
    }
    if (creditoExcedeInmueble(sn.monto_solicitado, sn.inm_valor_estimado)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    // Mejora en condiciones (destino 7): su Monto Solicitado también contra el Valor Estimado de la garantía.
    if (parseInt(sn.destino_id) === 7 && creditoExcedeInmueble(sn.mej_monto_solicitado, sn.inm_valor_estimado)) { alert(MENSAJE_CREDITO_INMUEBLE); return; }
    try {
      const r = await fetch(`${API}/santander/${prospectoId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospecto_id: prospectoId, ...sn }),
      });
      const data = await r.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, santander: sn }));
        alert('✓ Información de Santander guardada correctamente');
      } else {
        alert('Error: ' + data.error);
      }
    } catch (e) {
      alert('Error al guardar la información');
    }
  };

  const tieneCoac = !!formData.coacreditado?.nombres;
  const coacParticipaIngresos = formData.coacreditado?.participa_ingresos === true;

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const renderSiNo = (campo) => (
    <select
      value={sn[campo] === true ? 'true' : sn[campo] === false ? 'false' : ''}
      onChange={e => upd(campo, e.target.value === '' ? null : e.target.value === 'true')}
      style={st.input}
    >
      <option value="">-- Seleccionar --</option>
      <option value="true">Sí</option>
      <option value="false">No</option>
    </select>
  );

  const renderSelect = (campo, opciones) => (
    <select value={sn[campo] || ''} onChange={e => upd(campo, e.target.value)} style={st.input}>
      <option value="">-- Seleccionar --</option>
      {opciones.map(o => <option key={o.id} value={o.id}>{o.nombre}</option>)}
    </select>
  );

  const renderMoney = (label, campo, decimales = 2) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <div style={st.moneyWrap}>
        <span style={st.moneySign}>$</span>
        <input type="text" inputMode="decimal"
          value={focusedMoneyField === campo ? (sn[campo] ?? '') : formatearMoneda(sn[campo], decimales)}
          onFocus={() => setFocusedMoneyField(campo)}
          onBlur={() => setFocusedMoneyField(null)}
          onChange={e => upd(campo, limpiarMoneda(e.target.value, decimales))}
          style={{ ...st.inputNum, paddingLeft: 24 }} placeholder={decimales === 0 ? '0' : '0.00'} />
      </div>
    </div>
  );

  const renderMoneyReadOnly = (label, campo, decimales = 2) => (
    <div style={st.field}>
      <label style={st.label}>{label}</label>
      <input readOnly
        value={`$${parseFloat(sn[campo] || 0).toLocaleString('es-MX', { minimumFractionDigits: decimales, maximumFractionDigits: decimales })}`}
        style={{ ...st.input, ...st.readOnly }} />
    </div>
  );

  const renderInfoGeneral = (prefix, titulo) => {
    const esDivorciado = parseInt(sn[`${prefix}_estado_civil_id`]) === 4;
    return (
      <div style={st.card}>
        <h3 style={st.title}>{titulo}</h3>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>¿Es Cliente Santander?</label>
            {renderSiNo(`${prefix}_cliente_santander`)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Código Cliente Santander</label>
            <input type="number" value={sn[`${prefix}_codigo_cliente`] || ''}
              onChange={e => upd(`${prefix}_codigo_cliente`, e.target.value)} style={st.inputNum} />
          </div>
          <div style={st.field}>
            <label style={st.label}>Nacionalidad</label>
            <input readOnly value={nacionalidades.find(n => n.id === sn[`${prefix}_nacionalidad_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Escolaridad</label>
            {renderSelect(`${prefix}_escolaridad_id`, escolaridades)}
          </div>
        </div>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Tipo de Identificación</label>
            <input readOnly value={tiposIdentificacion.find(t => t.id === sn[`${prefix}_tipo_identificacion_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Número de Identificación</label>
            <input readOnly value={sn[`${prefix}_numero_identificacion`] || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          <div style={st.field}>
            <label style={st.label}>Estado Civil</label>
            <input readOnly value={estadosCiviles.find(ec => ec.id === sn[`${prefix}_estado_civil_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
          {parseInt(sn[`${prefix}_estado_civil_id`]) === 3 && (
            <div style={st.field}>
              <label style={st.label}>Régimen Matrimonial</label>
              <input readOnly value={regimenes.find(r => r.id === sn[`${prefix}_regimen_matrimonial_id`])?.nombre || ''}
                style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
            </div>
          )}
        </div>
        <div style={st.grid2}>
          {esDivorciado && (
            <div style={st.field}>
              <label style={st.label}>¿Paga pensión alimenticia?</label>
              {renderSiNo(`${prefix}_paga_pension`)}
            </div>
          )}
          <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '35%' }}>
            <label style={st.label}>Vive en Casa</label>
            <input readOnly value={vivesCasa.find(v => v.id === sn[`${prefix}_vive_casa_id`])?.nombre || ''}
              style={{ ...st.input, backgroundColor: '#f3f4f6' }} title="Se captura en la pestaña Titular / Coacreditado" />
          </div>
        </div>
      </div>
    );
  };

  const renderTrabajo = (prefix, titulo) => {
    const ocupId = parseInt(sn[`${prefix}_ocupacion_id`]);
    const esAsalariado = ocupId === 1 || ocupId === 2; // Empleado sector privado o público
    const esSectorPublico = ocupId === 2;
    return (
      <div style={st.card}>
        <h3 style={st.title}>{titulo}</h3>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Puesto</label>
            {renderSelect(`${prefix}_puesto_id`, puestos)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Ocupación/Actividad</label>
            {renderSelect(`${prefix}_ocupacion_id`, ocupaciones)}
          </div>
          {esSectorPublico && (
            <div style={st.field}>
              <label style={st.label}>Sector Público Detalle</label>
              {renderSelect(`${prefix}_sector_publico_id`, sectoresPublicos)}
            </div>
          )}
          <div style={st.field}>
            <label style={st.label}>Tipo de Contrato</label>
            {renderSelect(`${prefix}_tipo_contrato_id`, tiposContrato)}
          </div>
        </div>
        <div style={st.grid2}>
          <div style={st.field}>
            <label style={st.label}>Funciones y Actividades</label>
            <input value={sn[`${prefix}_funciones_actividades`] || ''}
              onChange={e => upd(`${prefix}_funciones_actividades`, e.target.value)} style={st.input} />
          </div>
          <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '60%' }}>
            <label style={st.label}>Giro de Empresa (Hipoteca Integral)</label>
            {renderSelect(`${prefix}_giro_empresa_id`, girosEmpresa)}
          </div>
        </div>
        {esAsalariado ? (
          <>
            <div style={st.grid2}>
              {renderMoney('Ingreso Total Mensual', `${prefix}_ingreso_total_mensual`)}
            </div>
            <div style={st.grid2}>
              <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '50%' }}>
                <label style={st.label}>¿Tiene ingresos extras (Independiente)?</label>
                {renderSiNo(`${prefix}_ingresos_mixtos`)}
              </div>
            </div>
            {sn[`${prefix}_ingresos_mixtos`] === true && (
              <>
                <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Ingresos extras como Independiente</p>
                <div style={st.grid4}>
                  {renderMoney('Ingreso Variable Mensual', `${prefix}_ingreso_variable_mensual`, 0)}
                  {renderMoney('Ingreso por Flujos en Cta. Cheques', `${prefix}_ingreso_flujos`, 0)}
                  {renderMoney('Otros Ingresos Anuales', `${prefix}_otros_ingresos_anuales`, 0)}
                  {renderMoneyReadOnly('Total Calculado (mensual)', `${prefix}_total_calculado`, 0)}
                </div>
                <div style={st.grid2}>
                  <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '75%' }}>
                    <label style={st.label}>Fuente de Otros Ingresos</label>
                    <input value={sn[`${prefix}_fuente_otros_ingresos`] || ''}
                      onChange={e => upd(`${prefix}_fuente_otros_ingresos`, e.target.value)} style={st.input} />
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>No Asalariado — ingresos variables</p>
            <div style={st.grid4}>
              {renderMoney('Ingreso Variable Mensual', `${prefix}_ingreso_variable_mensual`, 0)}
              {renderMoney('Ingreso por Flujos en Cta. Cheques', `${prefix}_ingreso_flujos`, 0)}
              {renderMoney('Otros Ingresos Anuales', `${prefix}_otros_ingresos_anuales`, 0)}
              {renderMoneyReadOnly('Total Calculado (mensual)', `${prefix}_total_calculado`, 0)}
            </div>
            <div style={st.grid2}>
              <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '75%' }}>
                <label style={st.label}>Fuente de Otros Ingresos</label>
                <input value={sn[`${prefix}_fuente_otros_ingresos`] || ''}
                  onChange={e => upd(`${prefix}_fuente_otros_ingresos`, e.target.value)} style={st.input} />
              </div>
            </div>
            <div style={st.grid2}>
              <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '50%' }}>
                <label style={st.label}>¿Tiene otros ingresos (Mixtos)?</label>
                {renderSiNo(`${prefix}_ingresos_mixtos`)}
              </div>
              {sn[`${prefix}_ingresos_mixtos`] === true && (
                <div style={{ maxWidth: isMobile ? '100%' : '50%' }}>
                  {renderMoney('Ingreso Total Mensual como Empleado', `${prefix}_ingreso_mensual_empleado`)}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderCuestionarioMedico = (prefix, titulo, mostrarCoberturaVida = false) => (
    <div style={st.card}>
      <h3 style={st.title}>{titulo}</h3>
      {mostrarCoberturaVida && (
        <div style={st.grid2}>
          <div style={st.field}>
            <label style={st.label}>¿Entra en cobertura de seguro de vida?</label>
            <SiNoRadio name="coac_cobertura_vida" value={sn.coac_cobertura_vida} onChange={v => upd('coac_cobertura_vida', v)} />
          </div>
        </div>
      )}
      <div style={st.grid2}>
        <div style={st.field}>
          <label style={st.label}>Tipo de Actividad</label>
          {renderSelect(`${prefix}_med_actividad_id`, actividadesSeguros)}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        {MED_PREGUNTAS.map((p) => {
          const campo = `${prefix}_med_${p.key}`;
          const val = sn[campo];
          return (
            <div key={campo} style={st.preguntaRow}>
              <span style={st.preguntaLabel}>
                <span style={st.grupoTag}>{p.grupo}</span>
                {p.key.replace(/[ab]/,'').replace('c','')}.  {p.texto}
              </span>
              <SiNoRadio name={campo} value={val} onChange={v => upd(campo, v)} defaultNo />
            </div>
          );
        })}
      </div>
    </div>
  );

  const destId = parseInt(sn.destino_id);
  const esLiquidez = destId === 5;
  const esSustitucion = destId === 9;
  const esMejora = destId === 7;

  const montoSolicitado = parseFloat(sn.monto_solicitado) || 0;
  const mostrarCuestionarioCualitativo = montoSolicitado >= 5000000;
  const ocupTitularId = parseInt(sn.titular_ocupacion_id);
  const esAsalariadoTitular = ocupTitularId === 1 || ocupTitularId === 2;
  const ocupCoacId = parseInt(sn.coac_ocupacion_id);
  const esAsalariadoCoac = ocupCoacId === 1 || ocupCoacId === 2;

  return (
    <div>
      {/* INFO GENERAL TITULAR */}
      {renderInfoGeneral('titular', '📋 Información General del Titular')}

      {/* TRABAJO TITULAR */}
      {renderTrabajo('titular', '💼 Información del Trabajo del Titular')}

      {/* INFO GENERAL COACREDITADO */}
      {tieneCoac && renderInfoGeneral('coac', '📋 Información General del Coacreditado')}

      {/* TRABAJO COACREDITADO */}
      {tieneCoac && coacParticipaIngresos && renderTrabajo('coac', '💼 Información del Trabajo del Coacreditado')}

      {/* CUESTIONARIO MÉDICO TITULAR */}
      {renderCuestionarioMedico('titular', '🏥 Cuestionario Médico del Titular')}

      {/* CUESTIONARIO MÉDICO COACREDITADO */}
      {tieneCoac && renderCuestionarioMedico('coac', '🏥 Cuestionario Médico del Coacreditado', true)}

      {/* AUTORIZACIÓN COMERCIALIZACIÓN */}
      <div style={st.card}>
        <h3 style={st.title}>📢 Autorización para Comercialización</h3>
        <div style={st.grid2}>
          <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '40%' }}>
            <label style={st.label}>Titular — Autoriza</label>
            {renderSiNo('titular_comercializacion')}
          </div>
          {tieneCoac && (
            <div style={{ ...st.field, maxWidth: isMobile ? '100%' : '40%' }}>
              <label style={st.label}>Coacreditado — Autoriza</label>
              {renderSiNo('coac_comercializacion')}
            </div>
          )}
        </div>
      </div>

      {/* SEGUROS */}
      <div style={st.card}>
        <h3 style={st.title}>🛡️ Seguros y Cumplimiento</h3>
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>Seguro de Vida</label>
            <select value={sn.seguro_vida || ''} onChange={e => upd('seguro_vida', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="zurich_santander">Zurich Santander</option>
              <option value="otra_aseguradora">Otra Aseguradora</option>
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Seguro de Daños</label>
            <select value={sn.seguro_danos || ''} onChange={e => upd('seguro_danos', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="zurich_santander">Zurich Santander</option>
              <option value="otra_aseguradora">Otra Aseguradora</option>
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Cláusulas Hipoteca Free</label>
            <select value={sn.hipoteca_free_cobertura || ''} onChange={e => upd('hipoteca_free_cobertura', e.target.value)} style={st.input}>
              <option value="">-- Seleccionar --</option>
              <option value="basica">Cobertura Básica</option>
              <option value="complementaria">Cobertura Complementaria</option>
            </select>
          </div>
        </div>
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>Funciones públicas últimos 12 meses</label>
            {renderSiNo('funciones_publicas_12meses')}
          </div>
          <div style={st.field}>
            <label style={st.label}>Actividades sensibles</label>
            {renderSiNo('actividades_sensibles')}
          </div>
          <div style={st.field}>
            <label style={st.label}>Relación familiar con funcionario Santander</label>
            {renderSiNo('relacion_familiar_funcionario')}
          </div>
        </div>
        {sn.relacion_familiar_funcionario === true && (
          <div style={st.grid2}>
            <div style={st.field}>
              <label style={st.label}>Parentesco</label>
              {renderSelect('parentesco_funcionario_id', parentescos)}
            </div>
          </div>
        )}
      </div>

      {/* CRÉDITO */}
      <div style={st.card}>
        <h3 style={st.title}>🏦 Información del Crédito</h3>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Oferta Vinculante</label>
            {renderSiNo('oferta_vinculante')}
          </div>
          <div style={st.field}>
            <label style={st.label}>Destino</label>
            {renderSelect('destino_id', destinos)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Producto</label>
            {renderSelect('producto_id', productos)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Esquema de Pagos</label>
            {renderSelect('esquema_pagos_id', esquemasPagos)}
          </div>
        </div>
        <div style={st.grid4}>
          <div style={st.field}>
            <label style={st.label}>Plazo</label>
            {renderSelect('plazo_id', plazos)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Programa de Financiamiento</label>
            {renderSelect('programa_financiamiento_id', programasFinanciamiento)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Tipo de Cofinanciamiento</label>
            {renderSelect('tipo_cofinanciamiento_id', tiposCofinanciamiento)}
          </div>
          <div style={st.field}>
            <div style={st.moneyWrap}>
              <label style={st.label}>Monto Solicitado</label>
              <span style={{ ...st.moneySign, top: 33 }}>$</span>
              <input type="text" inputMode="decimal"
                value={focusedMoneyField === 'monto_solicitado' ? (sn.monto_solicitado ?? '') : formatearMoneda(sn.monto_solicitado, 0)}
                onFocus={() => setFocusedMoneyField('monto_solicitado')}
                onBlur={() => setFocusedMoneyField(null)}
                onChange={e => upd('monto_solicitado', limpiarMoneda(e.target.value, 0))}
                style={{ ...st.inputNum, paddingLeft: 24 }} placeholder="0" />
            </div>
          </div>
        </div>
      </div>

      {/* CRÉDITO LIQUIDEZ */}
      {esLiquidez && (
        <div style={st.card}>
          <h3 style={st.title}>💰 Crédito Liquidez — Desglose</h3>
          <div style={st.grid4}>
            {renderMoney('Compra de Terreno', 'liq_compra_terreno')}
            {renderMoney('Pago Pasivos Bancos', 'liq_pago_pasivos_bancos')}
            {renderMoney('Pago Otros Pasivos', 'liq_pago_otros_pasivos')}
            {renderMoney('Otros', 'liq_otros')}
          </div>
        </div>
      )}

      {/* CRÉDITO SUSTITUCIÓN */}
      {esSustitucion && (
        <div style={st.card}>
          <h3 style={st.title}>🔄 Crédito Sustitución</h3>
          <div style={st.grid3}>
            <div style={st.field}>
              <label style={st.label}>Tipo de Liquidez</label>
              {renderSelect('sust_tipo_liquidez_id', tiposLiquidez)}
            </div>
            <div style={st.field}>
              <label style={st.label}>Exclusivo Coahuila</label>
              <select value={sn.sust_exclusivo_coahuila || ''} onChange={e => upd('sust_exclusivo_coahuila', e.target.value)} style={st.input}>
                <option value="">-- No aplica --</option>
                <option value="remodelacion">Remodelación</option>
                <option value="remodelacion_plus">Remodelación Plus</option>
              </select>
            </div>
          </div>
          <h4 style={st.subtitle}>Banco 1</h4>
          <div style={st.grid4}>
            <div style={st.field}>
              <label style={st.label}>Institución</label>
              <input value={sn.sust_banco1_institucion || ''} onChange={e => upd('sust_banco1_institucion', e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Destino</label>
              <input value={sn.sust_banco1_destino || ''} onChange={e => upd('sust_banco1_destino', e.target.value)} style={st.input} />
            </div>
            {renderMoney('Saldo', 'sust_banco1_saldo')}
            {renderMoney('Pago Mensual', 'sust_banco1_pago_mensual')}
          </div>
          <h4 style={st.subtitle}>Banco 2</h4>
          <div style={st.grid4}>
            <div style={st.field}>
              <label style={st.label}>Institución</label>
              <input value={sn.sust_banco2_institucion || ''} onChange={e => upd('sust_banco2_institucion', e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Destino</label>
              <input value={sn.sust_banco2_destino || ''} onChange={e => upd('sust_banco2_destino', e.target.value)} style={st.input} />
            </div>
            {renderMoney('Saldo', 'sust_banco2_saldo')}
            {renderMoney('Pago Mensual', 'sust_banco2_pago_mensual')}
          </div>
        </div>
      )}

      {/* MEJORA DE CONDICIONES */}
      {esMejora && (
        <div style={st.card}>
          <h3 style={st.title}>📈 Mejora de Condiciones</h3>
          <div style={st.grid4}>
            <div style={st.field}>
              <label style={st.label}>Origen del Crédito</label>
              <select value={sn.mej_origen_credito || ''} onChange={e => upd('mej_origen_credito', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                <option value="adquisicion">Adquisición</option>
                <option value="liquidez">Liquidez</option>
              </select>
            </div>
            <div style={st.field}>
              <label style={st.label}>Número de Crédito</label>
              <input value={sn.mej_numero_credito || ''} onChange={e => upd('mej_numero_credito', e.target.value)} style={st.input} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Moneda</label>
              <input value={sn.mej_moneda || ''} onChange={e => upd('mej_moneda', e.target.value)} style={st.input} placeholder="MXN/USD/UDIS" />
            </div>
            <div style={st.field}>
              <label style={st.label}>Plazo (años)</label>
              <input type="number" value={sn.mej_plazo || ''} onChange={e => upd('mej_plazo', e.target.value)} style={st.inputNum} />
            </div>
          </div>
          <div style={st.grid4}>
            <div style={st.field}>
              <label style={st.label}>Tasa (%)</label>
              <input type="number" step="0.01" value={sn.mej_tasa || ''} onChange={e => upd('mej_tasa', e.target.value)} style={st.inputNum} />
            </div>
            <div style={st.field}>
              <label style={st.label}>Fecha de Otorgamiento</label>
              <input type="date" value={sn.mej_fecha_otorgamiento || ''} onChange={e => upd('mej_fecha_otorgamiento', e.target.value)} style={st.input} />
            </div>
            {renderMoney('Importe Original', 'mej_importe')}
            <div style={st.field}>
              <label style={st.label}>¿Mejora Anterior?</label>
              {renderSiNo('mej_mejora_anterior')}
            </div>
          </div>
          {sn.mej_mejora_anterior === true && (
            <div style={st.grid2}>
              <div style={st.field}>
                <label style={st.label}>Fecha Mejora Anterior</label>
                <input type="date" value={sn.mej_fecha_mejora_anterior || ''} onChange={e => upd('mej_fecha_mejora_anterior', e.target.value)} style={st.input} />
              </div>
            </div>
          )}
          <div style={st.grid3}>
            <div style={st.field}>
              <label style={st.label}>Origen Financiamiento</label>
              <select value={sn.mej_origen_financiamiento || ''} onChange={e => upd('mej_origen_financiamiento', e.target.value)} style={st.input}>
                <option value="">-- Seleccionar --</option>
                <option value="tradicional">Tradicional</option>
                <option value="cofinavit">Cofinavit</option>
                <option value="apoyo_infonavit">Apoyo Infonavit</option>
              </select>
            </div>
            <div style={st.field}>
              <label style={st.label}>Tipo de Mejora</label>
              {renderSelect('mej_tipo_mejora_id', tiposMejora)}
            </div>
            <div style={st.field}>
              <label style={st.label}>Tipo de Liquidez</label>
              {renderSelect('mej_tipo_liquidez_id', tiposLiquidez)}
            </div>
          </div>
          <div style={st.grid3}>
            {renderMoney('Saldo Banco', 'mej_saldo_banco')}
            {renderMoney('Saldo Infonavit', 'mej_saldo_infonavit')}
            {renderMoney('Monto Solicitado', 'mej_monto_solicitado')}
          </div>
        </div>
      )}

      {/* DATOS INMUEBLE EN GARANTÍA */}
      <div style={st.card}>
        <h3 style={st.title}>🏠 Datos Inmueble a Otorgar en Garantía</h3>
        <div style={st.grid4}>
          {renderMoney('Valor Estimado', 'inm_valor_estimado', 0)}
          <div style={st.field}>
            <label style={st.label}>Tipo de Inmueble</label>
            {renderSelect('inm_tipo_inmueble_id', tiposInmueble)}
          </div>
          <div style={st.field}>
            <label style={st.label}>Entidad Federativa</label>
            <select
              value={sn.inm_entidad_federativa || ''}
              onChange={e => {
                upd('inm_entidad_federativa', e.target.value);
                upd('inm_alcaldia_municipio', '');
                cargarMunicipiosInmueble(e.target.value);
              }}
              style={st.input}
            >
              <option value="">-- Seleccionar --</option>
              {estadosInmueble.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div style={st.field}>
            <label style={st.label}>Alcaldía / Municipio</label>
            <select
              value={sn.inm_alcaldia_municipio || ''}
              onChange={e => upd('inm_alcaldia_municipio', e.target.value)}
              style={st.input}
            >
              <option value="">-- Seleccionar --</option>
              {municipiosInmueble.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div style={st.grid3}>
          <div style={st.field}>
            <label style={st.label}>Colonia / Fraccionamiento</label>
            <input value={sn.inm_colonia_fraccionamiento || ''} onChange={e => upd('inm_colonia_fraccionamiento', e.target.value)} style={st.input} />
          </div>
        </div>
      </div>

      {/* CUESTIONARIO CUALITATIVO — solo créditos > $5,000,000 */}
      {mostrarCuestionarioCualitativo && (
        <CuestionarioCualitativo
          participante="titular" titulo="📝 Cuestionario Cualitativo del Titular (Para Hipotecas de $5 millones en adelante)"
          esAsalariado={esAsalariadoTitular} sn={sn} upd={upd} st={st} isMobile={isMobile}
          estadosInmueble={estadosInmueble}
        />
      )}
      {mostrarCuestionarioCualitativo && tieneCoac && coacParticipaIngresos && (
        <CuestionarioCualitativo
          participante="coac" titulo="📝 Cuestionario Cualitativo del Coacreditado (Para Hipotecas de $5 millones en adelante)"
          esAsalariado={esAsalariadoCoac} sn={sn} upd={upd} st={st} isMobile={isMobile}
          estadosInmueble={estadosInmueble}
        />
      )}

      <div style={{ textAlign: 'right' }}>
        <button onClick={guardar} style={st.saveBtn}>💾 Guardar Información Santander</button>
      </div>
    </div>
  );
};

export default BancoSantander;
